import api from './api';

export interface LgDevice {
  id: number;
  external_id: string;
  nombre: string;
  device_type: string;
  ui_family: 'ac' | 'generic';
  modelo?: string | null;
  habitacion_id: number | null;
  artefacto_id: number | null;
  artefacto?: { nombre?: string; icono?: string; color_icono?: string } | null;
  capabilities?: string[];
  profile?: Record<string, unknown> | null;
  online: boolean;
  health_status?: string | null;
  power?: boolean | null;
  temperature?: number | null;
  setpoint?: number | null;
  hvac_mode?: string | null;
  fan_mode?: string | null;
  swing_mode?: string | null;
  state_cache?: Record<string, unknown>;
  /** Campos TV opcionales: el modal clona Samsung y tipa volume/mute; LG no los usa. */
  volume?: number | null;
  mute?: boolean | null;
  input_source?: string | null;
  enabled?: boolean;
  missing_from_cloud?: boolean;
  orden?: number;
  pin_required?: boolean;
  can_control?: boolean;
  can_view?: boolean;
}

export interface LgPolicyRow {
  lg_device_id: number;
  whitelist: boolean;
  blacklist: boolean;
  require_confirmation?: boolean;
}

export async function fetchLgAccount(): Promise<any> {
  const res = await api.get('/lg/account');
  return res.data;
}

export async function lgConnect(pat: string, country_code = 'AR'): Promise<any> {
  const res = await api.post('/lg/account/connect', { pat, country_code });
  return res.data;
}

export async function lgDisconnectAccount(): Promise<void> {
  await api.delete('/lg/account');
}

export async function lgPurgeAccount(): Promise<void> {
  await api.delete('/lg/account/credentials');
}

export async function lgSync(): Promise<any> {
  const res = await api.post('/lg/sync');
  return res.data;
}

export async function fetchLgAdminSummary(): Promise<{ total: number; unassigned_count: number }> {
  const res = await api.get('/lg/admin/summary');
  return res.data;
}

export async function fetchLgDevices(): Promise<LgDevice[]> {
  const res = await api.get('/lg/devices');
  return res.data || [];
}

export async function patchLgDevice(id: number, body: Record<string, unknown>): Promise<LgDevice> {
  const res = await api.patch(`/lg/devices/${id}`, body);
  return res.data;
}

export async function deleteLgDevice(id: number): Promise<void> {
  await api.delete(`/lg/devices/${id}`);
}

export async function reorderLgDevices(items: { id: number; orden: number }[]): Promise<void> {
  await api.put('/lg/devices/reorder', { items });
}

export async function lgControl(device_id: number, action: string, value?: unknown): Promise<any> {
  const res = await api.post('/lg/control', { device_id, action, value });
  return res.data;
}

/** Relee estado real desde ThinQ (1 llamada; sync de modal AC). */
export async function lgRefreshDevice(device_id: number): Promise<LgDevice> {
  const res = await api.post(`/lg/devices/${device_id}/refresh`);
  return res.data;
}

export interface LgActivityPoint {
  time_bucket: string;
  activity_ratio: number;
  temperatura_promedio: number | null;
  setpoint_promedio?: number | null;
}

/** Serie histórica actividad (+ temperatura en AC) para el modal — paridad Samsung. */
export async function fetchLgActivitySeries(params: {
  deviceId: number;
  startDate: string;
  endDate: string;
  interval: string;
}): Promise<{ ok: boolean; device_kind: string; data: LgActivityPoint[] }> {
  const res = await api.get('/lg/historical/activity-time-series', {
    params: {
      device_id: params.deviceId,
      start_date: params.startDate,
      end_date: params.endDate,
      interval: params.interval,
    },
  });
  return res.data;
}

export async function listLgPolicies(): Promise<LgPolicyRow[]> {
  try {
    const res = await api.get<{ policies: LgPolicyRow[] }>('/lg/ai-policies');
    return res.data.policies || [];
  } catch {
    return [];
  }
}

export async function getLgPolicy(deviceId: number): Promise<LgPolicyRow | null> {
  try {
    const res = await api.get<LgPolicyRow>(`/lg/devices/${deviceId}/ai-policy`);
    return res.data;
  } catch {
    return null;
  }
}

export async function upsertLgPolicy(
  deviceId: number,
  body: { whitelist?: boolean; blacklist?: boolean; require_confirmation?: boolean },
): Promise<LgPolicyRow> {
  const res = await api.put(`/lg/devices/${deviceId}/ai-policy`, body);
  return res.data;
}

export async function deleteLgPolicy(deviceId: number): Promise<void> {
  await api.delete(`/lg/devices/${deviceId}/ai-policy`);
}
